"use client";
/* eslint-disable @next/next/no-img-element */

import { useCallback, useEffect, useMemo, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, PlayCircle } from "lucide-react";

import { SharedImageLightbox, type SharedImageLightboxSlide } from "@/components/media/shared-image-lightbox";
import { appendVideoStartFragment } from "@/lib/courses/video-preview";
import { cn } from "@/utils/cn";

export type CourseMediaGalleryItem =
  | {
      id: string;
      type: "image";
      src: string;
      alt: string;
      thumbnailSrc?: string;
    }
  | {
      id: string;
      type: "video";
      src: string;
      title?: string;
      embedUrl?: string;
      thumbnailSrc?: string;
    };

type CourseMediaGalleryProps = {
  items: CourseMediaGalleryItem[];
  className?: string;
  showThumbnails?: boolean;
  thumbnailsViewport?: "all" | "desktop";
};

function clampIndex(index: number, total: number): number {
  if (total <= 0) return 0;
  if (index < 0) return 0;
  if (index >= total) return total - 1;
  return index;
}

export function CourseMediaGallery({
  items,
  className,
  showThumbnails = true,
  thumbnailsViewport = "all",
}: CourseMediaGalleryProps) {
  const hasMultiple = items.length > 1;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  const [loadedEmbedIds, setLoadedEmbedIds] = useState<Record<string, true>>({});
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: hasMultiple,
    align: "start",
    skipSnaps: false,
    dragFree: false,
  });

  useEffect(() => {
    setCurrentIndex((previous) => clampIndex(previous, items.length));
  }, [items.length]);

  const { lightboxSlides, itemToImageIndex, imageToItemIndex } = useMemo(() => {
    const slides: SharedImageLightboxSlide[] = [];
    const nextItemToImageIndex = new Map<number, number>();
    const nextImageToItemIndex: number[] = [];

    items.forEach((item, itemIndex) => {
      if (item.type !== "image") return;

      const imageIndex = slides.length;
      slides.push({
        id: item.id,
        src: item.src,
        alt: item.alt,
        title: item.alt,
      });
      nextItemToImageIndex.set(itemIndex, imageIndex);
      nextImageToItemIndex.push(itemIndex);
    });

    return {
      lightboxSlides: slides,
      itemToImageIndex: nextItemToImageIndex,
      imageToItemIndex: nextImageToItemIndex,
    };
  }, [items]);

  useEffect(() => {
    if (viewerIndex === null) return;
    if (viewerIndex >= lightboxSlides.length) {
      setViewerIndex(null);
    }
  }, [lightboxSlides.length, viewerIndex]);

  useEffect(() => {
    if (!emblaApi) return;

    const syncSelectedIndex = () => {
      setCurrentIndex(clampIndex(emblaApi.selectedScrollSnap(), items.length));
    };

    syncSelectedIndex();
    emblaApi.on("select", syncSelectedIndex);
    emblaApi.on("reInit", syncSelectedIndex);

    return () => {
      emblaApi.off("select", syncSelectedIndex);
      emblaApi.off("reInit", syncSelectedIndex);
    };
  }, [emblaApi, items.length]);

  useEffect(() => {
    if (!emblaApi || items.length === 0) return;
    const safeIndex = clampIndex(currentIndex, items.length);
    if (emblaApi.selectedScrollSnap() !== safeIndex) {
      emblaApi.scrollTo(safeIndex);
    }
  }, [currentIndex, emblaApi, items.length]);

  if (items.length === 0) {
    return (
      <div className={cn("rounded-2xl border border-[#d8cab2] bg-[#f4ede0] p-6", className)}>
        <div className="aspect-video rounded-xl border border-dashed border-[#d2c3ab] bg-[#efe4d2]" />
      </div>
    );
  }

  const safeCurrentIndex = clampIndex(currentIndex, items.length);

  const selectItem = useCallback(
    (index: number) => {
      const safeIndex = clampIndex(index, items.length);
      setCurrentIndex(safeIndex);
      emblaApi?.scrollTo(safeIndex);
    },
    [emblaApi, items.length]
  );

  const goPrevious = useCallback(() => {
    if (!emblaApi) {
      selectItem(safeCurrentIndex - 1);
      return;
    }
    emblaApi.scrollPrev();
  }, [emblaApi, safeCurrentIndex, selectItem]);

  const goNext = useCallback(() => {
    if (!emblaApi) {
      selectItem(safeCurrentIndex + 1);
      return;
    }
    emblaApi.scrollNext();
  }, [emblaApi, safeCurrentIndex, selectItem]);

  const loadVideoEmbed = useCallback((itemId: string) => {
    setLoadedEmbedIds((previous) => {
      if (previous[itemId]) return previous;
      return {
        ...previous,
        [itemId]: true,
      };
    });
  }, []);

  const openImageFullscreen = useCallback(
    (itemIndex: number) => {
      const imageIndex = itemToImageIndex.get(itemIndex);
      if (typeof imageIndex !== "number") return;
      setViewerIndex(imageIndex);
    },
    [itemToImageIndex]
  );

  const handleLightboxIndexChange = useCallback(
    (imageIndex: number) => {
      setViewerIndex(imageIndex);
      const itemIndex = imageToItemIndex[imageIndex];
      if (typeof itemIndex !== "number") return;
      setCurrentIndex(itemIndex);
      emblaApi?.scrollTo(itemIndex);
    },
    [emblaApi, imageToItemIndex]
  );

  return (
    <>
      <div data-testid="course-gallery" className={cn("space-y-3", className)}>
        <div className="group relative overflow-hidden rounded-2xl border border-[#d8cab2] bg-[#f4ede0]">
          <div ref={emblaRef} data-testid="course-gallery-viewport" className="overflow-hidden">
            <div className="flex touch-pan-y">
              {items.map((item, index) => {
                const isActive = index === safeCurrentIndex;
                const embedLoaded = item.type === "video" ? Boolean(loadedEmbedIds[item.id]) : false;

                return (
                  <div
                    key={item.id}
                    data-testid={`course-gallery-slide-${item.id}`}
                    data-active={isActive}
                    className="relative min-w-0 flex-[0_0_100%]"
                  >
                    <div className="relative aspect-video w-full">
                      {item.type === "image" ? (
                        <button
                          type="button"
                          onClick={() => openImageFullscreen(index)}
                          data-testid={`course-gallery-image-open-${item.id}`}
                          aria-label="Open image fullscreen"
                          className="h-full w-full cursor-zoom-in bg-transparent p-0 text-left"
                        >
                          <img
                            src={item.src}
                            alt={item.alt}
                            className="h-full w-full object-cover"
                            loading={index === 0 ? "eager" : "lazy"}
                            fetchPriority={index === 0 ? "high" : "auto"}
                            decoding="async"
                          />
                        </button>
                      ) : item.embedUrl ? (
                        isActive && embedLoaded ? (
                          <iframe
                            title={item.title || "Course preview"}
                            src={item.embedUrl}
                            loading="lazy"
                            data-gallery-interactive="true"
                            className="h-full w-full border-0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                          />
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              if (!isActive) {
                                selectItem(index);
                                return;
                              }
                              loadVideoEmbed(item.id);
                            }}
                            data-testid={`course-gallery-video-select-${item.id}`}
                            className="group relative h-full w-full cursor-pointer bg-[#e9dcc8] p-0 text-left"
                            aria-label={isActive ? `Play ${item.title || "course preview video"}` : `Select ${item.title || "course preview video"}`}
                          >
                            {item.thumbnailSrc ? (
                              <img
                                src={item.thumbnailSrc}
                                alt={item.title || "Course preview thumbnail"}
                                className="h-full w-full object-cover"
                                loading={index === 0 ? "eager" : "lazy"}
                                fetchPriority={index === 0 ? "high" : "auto"}
                                decoding="async"
                              />
                            ) : (
                              <div className="h-full w-full bg-[linear-gradient(135deg,#ded1bc,#c9b697)]" />
                            )}
                            <div className="absolute inset-0 bg-black/25 transition-colors group-hover:bg-black/35" />
                            <span className="absolute inset-0 flex items-center justify-center">
                              <span className="inline-flex items-center gap-2 rounded-full border border-white/60 bg-black/55 px-4 py-2 text-sm font-semibold text-white">
                                <PlayCircle className="h-5 w-5" />
                                {isActive ? "Play preview" : "Select preview"}
                              </span>
                            </span>
                          </button>
                        )
                      ) : isActive ? (
                        <video className="h-full w-full object-cover" controls preload="metadata" data-gallery-interactive="true" src={item.src} />
                      ) : (
                        <video className="h-full w-full object-cover" muted playsInline preload="metadata" src={appendVideoStartFragment(item.src)} />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {hasMultiple ? (
            <>
              <button
                type="button"
                onClick={goPrevious}
                data-testid="course-gallery-prev"
                aria-label="Previous preview"
                className="absolute left-3 top-1/2 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-[#d8cab2] bg-[#fffaf1]/95 text-[#334742] shadow-sm transition-colors hover:bg-white"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={goNext}
                data-testid="course-gallery-next"
                aria-label="Next preview"
                className="absolute right-3 top-1/2 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-[#d8cab2] bg-[#fffaf1]/95 text-[#334742] shadow-sm transition-colors hover:bg-white"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          ) : null}
        </div>

        {showThumbnails && hasMultiple ? (
          <div className={cn("overflow-x-auto pb-1", thumbnailsViewport === "desktop" ? "hidden lg:block" : undefined)}>
            <ul className="flex min-w-max snap-x gap-2">
              {items.map((item, index) => (
                <li key={item.id} className="snap-start">
                  <button
                    type="button"
                    onClick={() => selectItem(index)}
                    data-testid={`course-gallery-thumb-${item.id}`}
                    aria-pressed={index === safeCurrentIndex}
                    className={cn(
                      "relative h-20 w-24 overflow-hidden rounded-lg border bg-[#f5ecdd] transition-all lg:w-28",
                      index === safeCurrentIndex
                        ? "border-[#0f766e] ring-2 ring-[#0f766e]/20"
                        : "border-[#d2c4ad] hover:border-[#b69e77]"
                    )}
                  >
                    {item.thumbnailSrc ? (
                      <img src={item.thumbnailSrc} alt="" className="h-full w-full object-cover" loading="lazy" />
                    ) : item.type === "image" ? (
                      <img src={item.src} alt="" className="h-full w-full object-cover" loading="lazy" />
                    ) : item.type === "video" && !item.embedUrl ? (
                      <video
                        className="h-full w-full object-cover"
                        muted
                        playsInline
                        preload="metadata"
                        src={appendVideoStartFragment(item.src)}
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-[#ebe2d0] text-[#3f534d]">
                        <PlayCircle className="h-5 w-5" />
                      </div>
                    )}

                    {item.type === "video" ? (
                      <span className="absolute bottom-1 left-1 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-[0.08em] text-white">
                        Video
                      </span>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>

      <SharedImageLightbox
        open={viewerIndex !== null}
        slides={lightboxSlides}
        index={viewerIndex ?? 0}
        onClose={() => setViewerIndex(null)}
        onIndexChange={handleLightboxIndexChange}
      />
    </>
  );
}
